import * as React from 'react';

import { cn } from '../lib/utils';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './breadcrumb';

export interface BreadcrumbTrailItem {
  label: string;
  /**
   * The link element this crumb renders as — the consumer's own router link,
   * e.g. `<Link to="/income" />`. Omit it for the current page, and for any
   * ancestor that is not navigable.
   */
  render?: React.ReactElement;
}

export interface BreadcrumbTrailProps {
  /** Ancestors first, current page last. */
  items: BreadcrumbTrailItem[];
  className?: string;
}

/**
 * A breadcrumb trail built from a flat list, so callers don't restate the
 * primitives' nesting. Router-agnostic on purpose: `@loop3/ui` is consumed by
 * both the Vite SPAs and Next.js, so each crumb supplies its own link element
 * rather than this reaching for one.
 */
function BreadcrumbTrail({ items, className }: BreadcrumbTrailProps) {
  return (
    <Breadcrumb className={className}>
      <BreadcrumbList className="flex-nowrap gap-1.5 text-[length:inherit] text-inherit sm:gap-2">
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1;

          return (
            <React.Fragment key={`${item.label}-${index}`}>
              <BreadcrumbItem className={cn(isCurrent && 'min-w-0')}>
                {isCurrent || !item.render ? (
                  // The current page can be an arbitrarily long entity name, and the
                  // header is one line: truncate it and keep the full text on hover.
                  <BreadcrumbPage className="truncate text-inherit" title={item.label}>
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    asChild
                    className="whitespace-nowrap transition-opacity hover:text-inherit hover:opacity-100"
                  >
                    {React.cloneElement(item.render, {}, item.label)}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isCurrent && <BreadcrumbSeparator className="opacity-70 [&>svg]:size-3" />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export { BreadcrumbTrail };
